import React, { useState, useCallback } from 'react';

import { makeStyles, rgba } from 'design-system/styles';
import useShortcut from 'react-scale/hooks/useShortcut';
import { BoundingBox } from '@scale/shared/types/response/textcollection';

interface EditableBoundingBoxProps {
  value?: BoundingBox;
  onUpdate: (value: BoundingBox) => void;
  naturalWidth: number;
  naturalHeight: number;
  offsetWidth: number;
  offsetHeight: number;
  color?: string;
}

const EditableBoundingBox: React.FC<EditableBoundingBoxProps> = props => {
  const {
    value,
    onUpdate,
    naturalWidth,
    naturalHeight,
    offsetWidth,
    offsetHeight,
    color = '#D4354F',
  } = props;

  const classes = useEditableBoundingBoxStyles();

  const [resizeState, setResizeState] = useState<ResizeState>({ isDragging: false });

  const [focus, setFocus] = useState<{ target: ResizeTarget; key: string } | null>(null);

  const onKeyMove = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      if (!value) {
        return;
      }
      if (!focus || isKeyFocus(focus.key, event.key)) {
        setFocus({
          target: getArrowKeyTarget(event.key),
          key: event.key,
        });
      } else {
        const keyMove = getKeyMove(event.key);
        const nextValue = getResizeBoundingBox(value, {
          target: focus.target,
          dx: (keyMove.dx / offsetWidth) * naturalWidth,
          dy: (keyMove.dy / offsetHeight) * naturalHeight,
        });
        onUpdate(nextValue);
      }
    },
    [focus, setFocus, value, onUpdate, offsetWidth, offsetHeight, naturalWidth, naturalHeight],
  );

  useShortcut({ action: onKeyMove, trigger: 'up,right,left,down' });

  const onBlur = useCallback(() => {
    setFocus(null);
  }, [setFocus]);

  useShortcut({ action: onBlur, trigger: 'alt,option' });

  const getMouseDownHandler = (target: ResizeTarget) => {
    return (event: React.MouseEvent<unknown>) => {
      event.preventDefault();
      event.stopPropagation();
      setResizeState({
        isDragging: true,
        target,
        dragStartX: event.pageX,
        dragStartY: event.pageY,
        dragStartValue: value!,
      });
    };
  };

  const onRedrawMouseDownHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const { left: offsetLeft, top: offsetTop } = event.currentTarget.getBoundingClientRect();
    const newLeft = ((event.clientX - offsetLeft) / offsetWidth) * naturalWidth;
    const newTop = ((event.clientY - offsetTop) / offsetHeight) * naturalHeight;
    const newBox = {
      left: newLeft,
      top: newTop,
      width: 0,
      height: 0,
    };
    setResizeState({
      isDragging: true,
      target: ResizeTarget.SouthEast,
      dragStartX: event.pageX,
      dragStartY: event.pageY,
      dragStartValue: newBox,
    });
  };

  const onMouseMove = (event: React.MouseEvent<unknown>) => {
    if (resizeState.isDragging) {
      event.preventDefault();
      event.stopPropagation();
      const { target, dragStartX, dragStartY, dragStartValue } = resizeState;
      const dx = ((event.pageX - dragStartX) / offsetWidth) * naturalWidth;
      const dy = ((event.pageY - dragStartY) / offsetHeight) * naturalHeight;
      const nextValue = getResizeBoundingBox(dragStartValue, { target, dx, dy });
      onUpdate(nextValue);
    }
  };

  const onMouseUp = (event: React.MouseEvent<unknown>) => {
    if (resizeState.isDragging) {
      event.preventDefault();
      event.stopPropagation();
      setResizeState({ isDragging: false });
    }
  };

  // kinda a hack: if box is undefined, just fill these variables with junk, and depend
  // on making sure they aren't used.
  const box = value ?? { left: 0, top: 0, width: 0, height: 0 };
  const left = Math.round((box.left / naturalWidth) * offsetWidth);
  const top = Math.round((box.top / naturalHeight) * offsetHeight);
  const width = Math.max(Math.round((box.width / naturalWidth) * offsetWidth), 1);
  const height = Math.max(Math.round((box.height / naturalHeight) * offsetHeight), 1);
  const right = left + width;
  const bottom = top + height;

  const border = `1px solid ${color}`;
  const borderFocus = '2px solid #FB00FF';

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        background: 'transparent',
        cursor: 'crosshair',
      }}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseDown={onRedrawMouseDownHandler}
    >
      {value && (
        <>
          <div
            className={classes.box}
            style={{
              position: 'absolute',
              top,
              left,
              width,
              height,
              background: rgba(color, 0.15),
              borderTop: focus?.target === ResizeTarget.North ? borderFocus : border,
              borderRight: focus?.target === ResizeTarget.East ? borderFocus : border,
              borderBottom: focus?.target === ResizeTarget.South ? borderFocus : border,
              borderLeft: focus?.target === ResizeTarget.West ? borderFocus : border,
            }}
          />
          {!focus && (
            <>
              <div
                style={{
                  position: 'absolute',
                  top: top - 3,
                  left: left - 3,
                  width: right - left - 6,
                  height: bottom - top - 6,
                  cursor: 'move',
                }}
                onMouseDown={getMouseDownHandler(ResizeTarget.Center)}
              />
              <div
                className={classes.corner}
                style={{
                  position: 'absolute',
                  top: top - 3,
                  left: left - 3,
                  width: 6,
                  height: 6,
                  cursor: 'nwse-resize',
                  border,
                }}
                onMouseDown={getMouseDownHandler(ResizeTarget.NorthWest)}
              />
              <div
                className={classes.corner}
                style={{
                  position: 'absolute',
                  top: top - 3,
                  left: right - 3,
                  width: 6,
                  height: 6,
                  cursor: 'nesw-resize',
                  border,
                }}
                onMouseDown={getMouseDownHandler(ResizeTarget.NorthEast)}
              />
              <div
                className={classes.corner}
                style={{
                  position: 'absolute',
                  top: bottom - 3,
                  left: left - 3,
                  width: 6,
                  height: 6,
                  cursor: 'nesw-resize',
                  border,
                }}
                onMouseDown={getMouseDownHandler(ResizeTarget.SouthWest)}
              />
              <div
                className={classes.corner}
                style={{
                  position: 'absolute',
                  top: bottom - 3,
                  left: right - 3,
                  width: 6,
                  height: 6,
                  cursor: 'nwse-resize',
                  border,
                }}
                onMouseDown={getMouseDownHandler(ResizeTarget.SouthEast)}
              />
              <div
                className={classes.side}
                style={{
                  position: 'absolute',
                  top: top + 3,
                  left: left - 3,
                  width: 6,
                  height: height - 6,
                  cursor: 'ew-resize',
                }}
                onMouseDown={getMouseDownHandler(ResizeTarget.West)}
              />
              <div
                className={classes.side}
                style={{
                  position: 'absolute',
                  top: top + 3,
                  left: right - 3,
                  width: 6,
                  height: height - 6,
                  cursor: 'ew-resize',
                }}
                onMouseDown={getMouseDownHandler(ResizeTarget.East)}
              />
              <div
                className={classes.side}
                style={{
                  position: 'absolute',
                  top: top - 3,
                  left: left + 3,
                  width: width - 6,
                  height: 6,
                  cursor: 'ns-resize',
                }}
                onMouseDown={getMouseDownHandler(ResizeTarget.North)}
              />
              <div
                className={classes.side}
                style={{
                  position: 'absolute',
                  top: bottom - 3,
                  left: left + 3,
                  width: width - 6,
                  height: 6,
                  cursor: 'ns-resize',
                }}
                onMouseDown={getMouseDownHandler(ResizeTarget.South)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

EditableBoundingBox.displayName = 'EditableBoundingBox';

const useEditableBoundingBoxStyles = makeStyles({
  box: {
    boxSizing: 'border-box',
  },
  corner: {
    background: '#fff',
  },
  side: {},
});

enum ResizeTarget {
  Center = 'c',
  North = 'n',
  West = 'w',
  East = 'e',
  South = 's',
  NorthWest = 'nw',
  NorthEast = 'ne',
  SouthWest = 'sw',
  SouthEast = 'se',
}

interface DragStartState {
  isDragging: true;
  target: ResizeTarget;
  dragStartX: number;
  dragStartY: number;
  dragStartValue: BoundingBox;
}

interface DragEndState {
  isDragging: false;
}

type ResizeState = DragStartState | DragEndState;

interface ResizeParams {
  target: ResizeTarget;
  dx: number;
  dy: number;
}

function getResizeBoundingBox(box: BoundingBox, { target, dx, dy }: ResizeParams): BoundingBox {
  const topLeft = {
    x: box.left,
    y: box.top,
  };
  const bottomRight = {
    x: box.left + box.width,
    y: box.top + box.height,
  };
  switch (target) {
    case ResizeTarget.Center:
      topLeft.x += dx;
      topLeft.y += dy;
      bottomRight.x += dx;
      bottomRight.y += dy;
      break;
    case ResizeTarget.North:
      topLeft.y += dy;
      break;
    case ResizeTarget.West:
      topLeft.x += dx;
      break;
    case ResizeTarget.South:
      bottomRight.y += dy;
      break;
    case ResizeTarget.East:
      bottomRight.x += dx;
      break;
    case ResizeTarget.NorthWest:
      topLeft.x += dx;
      topLeft.y += dy;
      break;
    case ResizeTarget.NorthEast:
      bottomRight.x += dx;
      topLeft.y += dy;
      break;
    case ResizeTarget.SouthWest:
      topLeft.x += dx;
      bottomRight.y += dy;
      break;
    case ResizeTarget.SouthEast:
      bottomRight.x += dx;
      bottomRight.y += dy;
      break;
  }
  const top = Math.min(topLeft.y, bottomRight.y);
  const left = Math.min(topLeft.x, bottomRight.x);
  const bottom = Math.max(topLeft.y, bottomRight.y);
  const right = Math.max(topLeft.x, bottomRight.x);
  return {
    top,
    left,
    width: right - left,
    height: bottom - top,
  };
}

const isUpDown = new Set(['ArrowUp', 'ArrowDown']);

const isKeyFocus = (lastKey: string, nextKey: string): boolean => {
  return isUpDown.has(lastKey) !== isUpDown.has(nextKey);
};

const getArrowKeyTarget = (arrowKey: string): ResizeTarget => {
  switch (arrowKey) {
    case 'ArrowUp':
      return ResizeTarget.North;
    case 'ArrowRight':
      return ResizeTarget.East;
    case 'ArrowDown':
      return ResizeTarget.South;
    case 'ArrowLeft':
      return ResizeTarget.West;
    default:
      throw new TypeError(`invalid arrow key ${arrowKey}`);
  }
};

const getKeyMove = (key: string): { dx: number; dy: number } => {
  switch (key) {
    case 'ArrowUp':
      return { dx: 0, dy: -1 };
    case 'ArrowRight':
      return { dx: 1, dy: 0 };
    case 'ArrowDown':
      return { dx: 0, dy: 1 };
    case 'ArrowLeft':
      return { dx: -1, dy: 0 };
    default:
      return { dx: 0, dy: 0 };
  }
};

export default EditableBoundingBox;
